import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { PageForm } from '../components/PageForm/PageForm';
import { usePage } from '../hooks/usePage';
import {
  addPageComponent,
  createPage,
  removePageComponent,
  updatePage,
} from '../services/api/pages';
import { withFetchStatus } from '../services/notifier';

import type { FC } from 'react';
import type { PageResponse, PageInput } from '../types';

export const Page: FC = () => {
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const [page, setPage] = useState({} as PageResponse);

  const {
    data: entity,
    isFetching,
    isError,
    refetch,
  } = usePage(pageId === 'new' ? undefined : pageId);

  if (isError) {
    throw new Error(`Failed to load page ${pageId}`);
  }

  useEffect(() => {
    if (entity) {
      setPage(entity);
    }
  }, [entity]);

  async function onSubmit({
    id,
    ...pageDraft
  }: Partial<PageInput & { id?: string }>) {
    if (id) {
      await withFetchStatus(updatePage, {
        success: 'Page updated',
        error: 'Failed to update page',
      })(id, pageDraft);
      navigate('/pages');
    } else {
      const page = await withFetchStatus(createPage, {
        success: 'Page created',
        error: 'Failed to create page',
      })(pageDraft);

      navigate(`/pages/${page.id}`, { replace: true });
    }
  }

  async function handlePageUpdate({
    id,
    ...pageDraft
  }: Partial<PageInput> & { id: string }) {
    await withFetchStatus(updatePage, {
      success: 'Page updated',
      error: 'Failed to update page',
    })(id, pageDraft);

    await refetch();
  }

  async function addComponent(componentId: string, index: number) {
    await withFetchStatus(addPageComponent, {
      error: 'Failed to add component',
    })(page.id, componentId, index);

    await refetch();
  }

  async function removeComponent(componentId: string) {
    await withFetchStatus(removePageComponent, {
      error: 'Failed to remove component',
    })(page.id, componentId);

    await refetch();
  }

  return (
    <PageForm
      page={page}
      onSubmit={onSubmit}
      updatePage={handlePageUpdate}
      addComponent={addComponent}
      removeComponent={removeComponent}
      isLoading={isFetching && !page.id}
      refetchPage={refetch}
    />
  );
};
